import { Euro, GraduationCap, FolderCheck, FileText, BookOpen, FileSignature } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const fmtDate = (d) => (d ? (/^\d{4}-/.test(d) ? new Date(d).toLocaleDateString('fr-FR') : d) : '');
const fmtMontant = (m) => Number(m || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const detail = (p) => [fmtMontant(p.montant), fmtDate(p.date) && `le ${fmtDate(p.date)}`, p.mode].filter(Boolean).join(' · ');

const STYLES = {
  vert: 'bg-green-100 text-green-700 border-green-300',
  rouge: 'bg-red-100 text-red-700 border-red-300',
  orange: 'bg-amber-100 text-amber-700 border-amber-300',
};

const Picto = ({ icon: Icon, label, color, tip, testId, small }) => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>
      <span
        className={`inline-flex items-center gap-1 rounded-full border font-semibold ${STYLES[color]} ${small ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}`}
        data-testid={testId}
        data-status={color}
      >
        <Icon className={small ? 'h-3 w-3' : 'h-3.5 w-3.5'} /> {label}
      </span>
    </TooltipTrigger>
    <TooltipContent className="bg-[#0F172A] text-white max-w-[260px]">{tip}</TooltipContent>
  </Tooltip>
);

const dossierTip = (c) => {
  const manquants = c.dossier_manquants || [];
  if (!manquants.length) return `Dossier complet : les ${c.dossier_total} documents obligatoires sont fournis`;
  const refuses = c.dossier_refuses || [];
  const absents = manquants.filter(m => !refuses.includes(m));
  return [absents.length && `Manquant(s) : ${absents.join(', ')}`, refuses.length && `Refusé(s) : ${refuses.join(', ')}`].filter(Boolean).join(' — ');
};

const ETAPES_SIGNATURE = [
  { cle: 'devis', label: 'Devis', icon: FileText, ok: 'Devis accepté', attente: 'Devis envoyé, en attente de signature' },
  { cle: 'pif', label: 'PIF', icon: BookOpen, ok: 'PIF signé', attente: 'PIF envoyé, en attente de signature' },
  { cle: 'convention', label: 'Convention', icon: FileSignature, ok: 'Convention signée', attente: 'Convention envoyée, en attente de signature' },
];

const tipSignature = (etape, s) => {
  const base = s.statut === 'signe' ? etape.ok : etape.attente;
  const date = fmtDate(s.date);
  return [base, s.numero && `n° ${s.numero}`, date && `${s.statut === 'signe' ? 'le' : 'envoyé le'} ${date}`].filter(Boolean).join(' · ');
};

// Devis / PIF / Convention : vert = signé, orange = en attente, rien = pas encore envoyé
export const SignaturePictos = ({ signatures, userId, small = false }) => (
  <>
    {ETAPES_SIGNATURE.map(etape => {
      const s = signatures?.[etape.cle];
      if (!s) return null;
      return (
        <Picto
          key={etape.cle}
          icon={etape.icon}
          label={etape.label}
          color={s.statut === 'signe' ? 'vert' : 'orange'}
          tip={tipSignature(etape, s)}
          testId={`picto-${etape.cle}${small ? '-m' : ''}-${userId}`}
          small={small}
        />
      );
    })}
  </>
);

export const PaiementPictos = ({ candidat, small = false }) => {
  const sfx = small ? '-m' : '';
  const tests = candidat.paiement_tests;
  const prepa = candidat.paiement_prepa;
  const testsPaye = tests?.statut === 'paye';
  const nbManquants = (candidat.dossier_manquants || []).length;
  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        {candidat.dossier_manquants && (
          <Picto
            icon={FolderCheck}
            label={nbManquants ? `Dossier · ${nbManquants}` : 'Dossier'}
            color={nbManquants ? 'orange' : 'vert'}
            tip={dossierTip(candidat)}
            testId={`picto-dossier${sfx}-${candidat.user_id}`}
            small={small}
          />
        )}
        <Picto
          icon={Euro}
          label="Tests"
          color={testsPaye ? 'vert' : 'rouge'}
          tip={testsPaye ? `Tests payés : ${detail(tests)}` : tests ? `Tests non payés (paiement commencé le ${fmtDate(tests.date)} mais pas terminé)` : 'Tests non payés'}
          testId={`picto-tests${sfx}-${candidat.user_id}`}
          small={small}
        />
        {(candidat.fait_prepa ?? prepa) && (
          <Picto
            icon={GraduationCap}
            label="Prépa"
            color={prepa?.statut === 'paye' ? 'vert' : 'rouge'}
            tip={prepa?.statut === 'paye' ? `Inscrit à la préparation et payé : ${detail(prepa)}` : 'Fait la préparation : non payée'}
            testId={`picto-prepa${sfx}-${candidat.user_id}`}
            small={small}
          />
        )}
        <SignaturePictos signatures={candidat.signatures} userId={candidat.user_id} small={small} />
      </div>
    </TooltipProvider>
  );
};
